import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { ReceiptTypeBadge, statusLabel } from "./ReceiptTable";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[6%]" },
  { key: "type", name: "Lloji", className: "w-[8%]" },
  { key: "submitted", name: "Krijuar", sort_value: "created_at", className: "w-[13%]" },
  { key: "user", name: "Përdoruesi", sort_value: "first_name", className: "w-[20%]" },
  { key: "nui", name: "NUI", sort_value: "NUI", className: "w-[14%]" },
  { key: "vat", name: "TVSH", sort_value: "vat_amount", className: "w-[11%]", align: "right" },
  { key: "total", name: "Total me TVSH", sort_value: "total_with_vat", className: "w-[16%]", align: "right" },
  { key: "status", name: "Statusi", className: "w-[12%]" },
];

const STATUS_PILL = {
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  pending: "bg-blue-50 text-blue-700 ring-blue-200",
  flagged: "bg-red-50 text-red-700 ring-red-200",
  rejected: "bg-red-50 text-red-700 ring-red-200",
  draft: "bg-gray-100 text-gray-700 ring-gray-200",
  updated: "bg-amber-50 text-amber-700 ring-amber-200",
};

const formatMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return `€${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
};

const StatusPill = ({ status }) => {
  const key = status?.toLowerCase();
  const cls = STATUS_PILL[key] || "bg-gray-100 text-gray-700 ring-gray-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-md text-xs font-semibold ring-1 ring-inset ${cls}`}
    >
      <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-current opacity-60" />
      {statusLabel(status) || "—"}
    </span>
  );
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-4 w-9 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-24 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-16 bg-gray-200 rounded ml-auto" /></td>
    <td className="px-6 py-4"><div className="h-3 w-20 bg-gray-200 rounded ml-auto" /></td>
    <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function ApprovedReceiptTable({
  receipts,
  isLoading,
  params = {},
  onSort,
  onRowClick,
}) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} ${
                      col.align === "right" ? "text-right" : "text-left"
                    } px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!receipts || receipts.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : receipts?.map((r) => (
                  <tr
                    key={r.receipt_id}
                    onClick={() => onRowClick?.(r)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                  >
                    <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                      #{r.receipt_id}
                    </td>
                    <td className="px-6 py-3.5">
                      <ReceiptTypeBadge type={r.type} />
                    </td>
                    <td className="px-6 py-3.5 text-gray-700 whitespace-nowrap tabular-nums text-xs">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="font-medium text-gray-900 truncate">
                        {(r.first_name || r.last_name)
                          ? `${r.first_name || ""} ${r.last_name || ""}`.trim()
                          : "—"}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-gray-700 tabular-nums">
                      {r.NUI || "—"}
                    </td>
                    <td className="px-6 py-3.5 text-right text-gray-700 tabular-nums">
                      {formatMoney(r.vat_amount)}
                    </td>
                    <td className="px-6 py-3.5 text-right text-gray-900 font-semibold tabular-nums">
                      {formatMoney(r.total_with_vat)}
                    </td>
                    <td className="px-6 py-3.5">
                      <StatusPill status={r.status} />
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!receipts || receipts.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          receipts?.map((r) => (
            <button
              key={r.receipt_id}
              type="button"
              onClick={() => onRowClick?.(r)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {(r.first_name || r.last_name)
                      ? `${r.first_name || ""} ${r.last_name || ""}`.trim()
                      : "—"}
                  </div>
                  <div className="text-xs text-gray-500 truncate tabular-nums">
                    {r.NUI ? `NUI ${r.NUI}` : "—"}
                  </div>
                </div>
                <StatusPill status={r.status} />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span className="tabular-nums">{formatDate(r.created_at)}</span>
                <span className="text-gray-900 font-semibold tabular-nums">
                  {formatMoney(r.total_with_vat)}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
